/**
 * /analytics — Activity chart. Cycle 16.
 *
 * Stacked bars per day: inbound (light) + outbound (dark). The chart
 * rendering is now owned by the shared ActivityChart component. This
 * page keeps its range picker, totals summary, and error banner.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { ActivityChart } from "../../../components/activity-chart.js";

interface Row {
  day: string;
  direction: "inbound" | "outbound";
  count_messages: number;
}

export const Route = createFileRoute("/_app/analytics/")({
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [days, setDays] = useState<number>(30);
  const [error, setError] = useState<string | null>(null);

  // Fetch rows only for the totals summary; the chart itself re-fetches
  // independently via ActivityChart (workspace-wide, no igAccountId).
  useEffect(() => {
    void load(days);
  }, [days]);

  async function load(d: number) {
    setError(null);
    try {
      const r = await api.get<{
        days: number;
        rows: Row[];
        chConfigured?: boolean;
        chError?: boolean;
      }>(`/analytics/messages-daily?days=${d}`);
      setRows(r.data.rows);
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to load analytics. Is ClickHouse reachable?");
      setRows([]);
    }
  }

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc[r.direction] += Number(r.count_messages);
          return acc;
        },
        { inbound: 0, outbound: 0 } as { inbound: number; outbound: number },
      ),
    [rows],
  );

  return (
    <section style={{ maxWidth: 880, margin: "0 auto" }}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 28 }}>Activity</h1>
        <select
          className="brut-input"
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          style={{ width: 130 }}
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </header>
      <p style={{ color: "var(--muted)", marginTop: 0, marginBottom: 24 }}>
        Inbound vs outbound messages per day. Includes auto-replies, broadcasts,
        sequences, and human-agent sends.
      </p>

      {error ? (
        <div
          className="brut-card"
          style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}
        >
          {error}
        </div>
      ) : null}

      <div
        className="brut-card"
        style={{ padding: 14, display: "grid", gap: 12 }}
      >
        <div style={{ display: "flex", gap: 24, fontSize: 13 }}>
          <div>
            <div style={{ color: "var(--muted)" }}>Inbound</div>
            <div style={{ fontWeight: 800, fontSize: 22 }}>{totals.inbound}</div>
          </div>
          <div>
            <div style={{ color: "var(--muted)" }}>Outbound</div>
            <div style={{ fontWeight: 800, fontSize: 22 }}>{totals.outbound}</div>
          </div>
          <div>
            <div style={{ color: "var(--muted)" }}>Total</div>
            <div style={{ fontWeight: 800, fontSize: 22 }}>
              {totals.inbound + totals.outbound}
            </div>
          </div>
        </div>

        <ActivityChart days={days} />
      </div>
    </section>
  );
}
